import {useCallback, useEffect, useRef, useState} from 'react';
import {useHistory, useLocation, useParams} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {Alert, AlertTitle, Box, Button, Stack, Typography} from '@mui/material';
import axios from 'axios';
import moment from 'moment';
import {v4 as uuidv4} from 'uuid';
import './attendance-tracker.daily-care-log.screen.scss';
import {AttendanceRecordSummary} from '../../../../components/attendance-tracker/attendance-record-summary';
import {DailyCareLogFormFooterButtons} from '../../../../components/daily-care-log/handle-task-form/daily-care-log-form-footer-buttons';
import {useAuth} from '../../../../contexts/auth/context';
import {useDailyCareLog} from '../../../../contexts/daily-care-log/context';
import {useSnackBar} from '../../../../contexts/snack-bar/context';
import {useAttendanceTrackerStatus} from '../../../../contexts/attendance-tracker-status/context';
import {ScreenPaths} from '../../../../enums';
import {AttendanceRecordType} from '../../../../backend/enums/attendance-record-type';
import {AttendanceTrackerRecordTypeConflict} from '../../../../backend/responses/attendance-tracker-record';
import {useGeolocation} from '../../../../hooks/use-geolocation';
import {translations} from '../../../../i18n/translations';
import {EntityAttendanceTrackerRecordsRepository} from '../../../../repositories/entity-attendance-tracker-records';
import {getLocationPermissionHelp} from '../../../../utils/functions/location-permission-help';

/** Current time, updated every second */
const useNow = () => {
  const [now, setNow] = useState(moment());

  useEffect(() => {
    const interval = setInterval(() => setNow(moment()), 1000);
    return () => clearInterval(interval);
  }, []);

  return now;
};

export default function NewAttendanceRecordDailyCareLogScreen() {
  const {t} = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const {entityId}: {entityId: string} = useParams();
  const {state} = useAuth();
  const dailyCareLogContext = useDailyCareLog();
  const now = useNow();
  const {permission, position, error, requestPermission} = useGeolocation();
  const snackBarContext = useSnackBar();
  const {refresh: refreshAttendanceStatus} = useAttendanceTrackerStatus();
  const texts = translations.daily_care_log.attendance_tracker.new;

  /** The type of the next record: entries and exits alternate. Unknown until the last record is loaded. */
  const [nextType, setNextType] = useState<AttendanceRecordType | undefined>(undefined);
  const [hasLoadingError, setHasLoadingError] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  /** Identifies this attempt, so a retry of the same record is not registered twice */
  const clientRequestIdRef = useRef(uuidv4());

  const searchParams = new URLSearchParams(location.search);
  const date = searchParams.get('date');
  const isEntry = nextType !== AttendanceRecordType.Exit;
  const user = state.session?.currentUser;
  const entity = state.session?.selectedAffiliation?.entity;

  // The header says what is going to be registered, once it is known
  useEffect(() => {
    dailyCareLogContext.setNavigationHeader(
      true,
      nextType
        ? t(isEntry ? texts.title_entry : texts.title_exit)
        : t(translations.daily_care_log.attendance_tracker.title)
    );
    dailyCareLogContext.toggleBottomNavigation(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nextType]);

  useEffect(() => {
    return () => {
      dailyCareLogContext.setNavigationHeader(false);
      dailyCareLogContext.toggleBottomNavigation(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Find out if this is an entry or an exit, from the last record of the user
  const loadNextType = useCallback(async () => {
    setHasLoadingError(false);

    try {
      const {nextType: type} =
        await EntityAttendanceTrackerRecordsRepository.instance.getLastRecord(entityId);
      setNextType(type);
    } catch (e) {
      console.error(e);
      setHasLoadingError(true);
    }
  }, [entityId]);

  useEffect(() => {
    loadNextType();
  }, [loadNextType]);

  const goBack = () =>
    history.push(
      `${ScreenPaths.DailyCareLog}/${entityId}${ScreenPaths.DailyCareLog_AttendanceTracker}${date ? `?date=${date}` : ''}`
    );

  const handleRegister = async () => {
    if (!position || !nextType) {
      return;
    }

    setIsSaving(true);
    try {
      await EntityAttendanceTrackerRecordsRepository.instance.registerRecord(entityId, {
        type: nextType,
        clientRegisteredAt: new Date().toISOString(),
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        location: {
          latitude: position.latitude,
          longitude: position.longitude,
          accuracy: position.accuracy,
          capturedAt: new Date(position.timestamp).toISOString(),
        },
        clientRequestId: clientRequestIdRef.current,
      });

      await refreshAttendanceStatus();

      snackBarContext.setDailyCareLogToastNotification({
        severity: 'success',
        message: t(isEntry ? texts.success_entry : texts.success_exit),
      });
      goBack();
    } catch (e) {
      console.error(e);

      if (
        axios.isAxiosError<AttendanceTrackerRecordTypeConflict>(e) &&
        e.response?.status === 409
      ) {
        // The last record changed (e.g. registered in another device): show what is expected now
        setNextType(e.response.data.nextType);
        clientRequestIdRef.current = uuidv4();
        snackBarContext.setDailyCareLogToastNotification({
          severity: 'warning',
          message: t(texts.type_conflict),
        });
      } else {
        snackBarContext.setDailyCareLogToastNotification({
          severity: 'error',
          message: t(texts.save_error),
        });
      }
    } finally {
      setIsSaving(false);
    }
  };

  const permissionBanner = () => {
    if (permission === 'unsupported') {
      return (
        <Alert severity="error">
          <AlertTitle>{t(texts.permission.unsupported_title)}</AlertTitle>
          {t(texts.permission.unsupported_description)}
        </Alert>
      );
    }

    if (permission === 'denied') {
      return (
        <Alert severity="error">
          <AlertTitle>{t(texts.permission.denied_title)}</AlertTitle>
          {t(texts.permission.denied_description)}
          <Typography component="p" marginTop="8px" fontWeight={500}>
            {t(texts.permission.help[getLocationPermissionHelp()])}
          </Typography>
        </Alert>
      );
    }

    if (permission === 'prompt') {
      return (
        <Alert severity="warning" sx={{'& .MuiAlert-message': {flex: 1}}}>
          {/* Two columns (text and button) when there is room, stacked on small screens */}
          <Stack
            direction={{xs: 'column', sm: 'row'}}
            alignItems={{xs: 'stretch', sm: 'center'}}
            justifyContent="space-between"
            gap="12px"
          >
            <Box>
              <AlertTitle>{t(texts.permission.prompt_title)}</AlertTitle>
              {t(texts.permission.prompt_description)}
            </Box>
            <Button
              variant="contained"
              color="warning"
              size="small"
              onClick={requestPermission}
              sx={{flexShrink: 0, alignSelf: {xs: 'flex-end', sm: 'center'}}}
            >
              {t(texts.permission.allow)}
            </Button>
          </Stack>
        </Alert>
      );
    }

    return null;
  };

  return (
    <Box className="new-attendance-record">
      <Stack className="new-attendance-record-content" gap="16px">
        {hasLoadingError && (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={loadNextType}>
                {t(texts.retry)}
              </Button>
            }
          >
            {t(texts.loading_error)}
          </Alert>
        )}

        {permissionBanner()}

        {/* Purpose of the location data */}
        <Alert severity="info" variant="outlined">
          {t(texts.purpose)}
        </Alert>

        {/* What is going to be registered */}
        <AttendanceRecordSummary
          type={nextType}
          isTypeLoading={!hasLoadingError}
          user={user}
          entityName={entity?.name}
          dateTime={now.format('DD/MM/YYYY HH:mm:ss')}
          location={position}
          locationFallback={permission === 'granted' ? t(texts.waiting) : '-'}
        >
          {permission === 'granted' && error && !position && (
            <Alert severity="warning">{t(texts.unavailable)}</Alert>
          )}

          {position && (
            <Alert severity="info">
              {t(texts.accuracy_info, {current: Math.round(position.accuracy)})}
            </Alert>
          )}
        </AttendanceRecordSummary>
      </Stack>

      <DailyCareLogFormFooterButtons
        canWrite
        editMode
        isCancelDisabled={isSaving}
        isSaveDisabled={!position || !nextType}
        isSaveLoading={isSaving}
        saveLabel={t(isEntry ? texts.save_entry : texts.save_exit)}
        onCancel={goBack}
        onSave={handleRegister}
        onEdit={() => {}}
      />
    </Box>
  );
}
